import { graphql, useStaticQuery } from 'gatsby';
import React, { FC, useContext, useState } from 'react';
import { useLocation } from 'react-use';

import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import aligned from '@ui/styled/Aligned';
import padded from '@ui/styled/Padded';

import useFetchPhoto from '@hooks/useFetchPhoto';

import { keysToCamel } from '@utils/butter';
import { exists } from '@utils/general';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepProps } from '@folx-types/flow-steps';

import MemberIdInfoStep, {
	IIdInfoStepContent,
	IMemberIdInfoStepProps,
} from './MemberIdInfoStep.component';

interface IMemberIdInfoStepPageProps extends IFlowStepProps {}

const AlignedRow = aligned(FlexRow);
const PaddedRow = padded(FlexRow);

const MemberIdInfoStepPage: FC<IMemberIdInfoStepPageProps> = ({ path, stepKey }) => {
	const location = useLocation();
	const showSocialSecurityCopy = location.pathname?.includes('hrt_t_sub') ?? false;

	const [loading, setLoading] = useState(true);

	const { handleNavNext } = useContext(FlowContext);

	useFetchPhoto({
		imageType: 'Photo ID',
		handleError: () => setLoading(false),
		callback: (photo) => (exists(photo) ? handleNavNext(stepKey) : setLoading(false)),
	});

	const { pageContent } = useStaticQuery<IMemberIdInfoStepProps>(
		graphql`
			{
				pageContent: butterPage(slug: { eq: "id-info" }) {
					title
					description
					document {
						title
						description
					}
					selfie {
						title
						description
					}
					social_security_number {
						title
						description
					}
					security_disclaimer {
						title
						description
					}
				}
			}
		`
	);

	const refinedContent = keysToCamel(pageContent) as IIdInfoStepContent;
	const skeleton = (
		<CenteredGridItem>
			<FlexColumn gap="md">
				<FlexColumn gap="sm">
					<Skeleton structure="single" height="lgxl" fullWidth />
					<Skeleton structure="column" repeated={3} gap="xs" height="sm" fullWidth />
				</FlexColumn>

				{new Array(showSocialSecurityCopy ? 4 : 3).fill('').map((_, id) => (
					<FlexColumn gap="xs" key={`skeleton ${id}`}>
						<AlignedRow gap="sm" alignItems="center">
							<Skeleton height="lg" width="lg" />
							<Skeleton height="md" width="xxxxxl" />
						</AlignedRow>
						<PaddedRow paddingLeft="lgxl">
							<Skeleton structure="column" repeated={2} gap="xs" height="sm" fullWidth />
						</PaddedRow>
					</FlexColumn>
				))}
				<Skeleton height="lg" fullWidth />
			</FlexColumn>
		</CenteredGridItem>
	);

	return (
		<LoadingWrapper skeleton={skeleton} loading={loading}>
			<MemberIdInfoStep
				path={path}
				stepKey={stepKey}
				pageContent={refinedContent}
				showSocialSecurityCopy={showSocialSecurityCopy}
			/>
		</LoadingWrapper>
	);
};

export default MemberIdInfoStepPage;
